import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Card component for banlist display
const BanlistCard = ({ card }) => {
  const [showDesc, setShowDesc] = useState(false);
  
  const getTypeColor = (type) => {
    if (type?.includes("Spell")) return "from-green-500 to-teal-600";
    if (type?.includes("Trap")) return "from-pink-500 to-purple-600";
    if (type?.includes("Fusion")) return "from-purple-500 to-violet-600";
    if (type?.includes("Synchro")) return "from-gray-100 to-gray-300";
    if (type?.includes("Xyz")) return "from-gray-800 to-black";
    if (type?.includes("Link")) return "from-blue-500 to-cyan-500";
    if (type?.includes("Ritual")) return "from-blue-600 to-indigo-700";
    return "from-yellow-500 to-orange-500";
  };

  return (
    <div 
      className="relative group cursor-pointer"
      onMouseEnter={() => setShowDesc(true)}
      onMouseLeave={() => setShowDesc(false)}
    >
      <div className={`w-full aspect-[421/614] rounded-lg overflow-hidden bg-gradient-to-br ${getTypeColor(card.type)} p-0.5`}>
        {card.image ? (
          <img 
            src={card.image} 
            alt={card.name}
            className="w-full h-full object-cover rounded-md"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gray-800 rounded-md flex items-center justify-center">
            <span className="text-xs text-gray-500 text-center px-1">{card.name}</span>
          </div>
        )}
      </div>
      
      {showDesc && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900/95 border border-purple-500/50 rounded-lg shadow-xl">
          <h4 className="font-bold text-white text-sm mb-1">{card.name}</h4>
          <p className="text-xs text-purple-400 mb-2">{card.type}</p>
          <p className="text-xs text-gray-300 line-clamp-4">{card.desc}</p>
        </div>
      )}
      
      <p className="text-xs text-gray-400 text-center mt-1 truncate">{card.name}</p>
    </div>
  );
};

// Section component
const BanlistSection = ({ title, cards, color, icon }) => {
  const [expanded, setExpanded] = useState(true);
  
  const colorClasses = {
    red: "from-red-500 to-red-700 border-red-500/50",
    yellow: "from-yellow-500 to-orange-600 border-yellow-500/50",
    green: "from-green-500 to-teal-600 border-green-500/50"
  };

  return (
    <div className="mb-8">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 glass-card rounded-xl hover:bg-purple-900/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            {icon}
          </div>
          <div className="text-left">
            <h3 className="font-bold text-white text-lg">{title}</h3>
            <p className="text-gray-400 text-sm">{cards.length} cartas</p>
          </div>
        </div>
        <svg 
          className={`w-6 h-6 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      
      {expanded && cards.length > 0 && (
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2 p-4 bg-purple-900/20 rounded-xl">
          {cards.map((card) => (
            <BanlistCard key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
};

const BanlistPage = () => {
  const [banlist, setBanlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState("tcg_actual");
  const [selectedDate, setSelectedDate] = useState("");
  const [tcgDates, setTcgDates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const FORMATS = [
    { id: "tcg_actual", name: "TCG Actual (Feb 2026)", color: "#dc2626", icon: "🌎" },
    { id: "tcg", name: "TCG Histórico", color: "#7c3aed", icon: "📅" },
    { id: "edison", name: "Edison Format", color: "#d4af37", icon: "⚡" },
    { id: "goat", name: "GOAT Format", color: "#22c55e", icon: "🐐" },
    { id: "hat", name: "HAT Format", color: "#06b6d4", icon: "🎩" },
    { id: "speed_duel", name: "Speed Duel", color: "#f59e0b", icon: "🏎️" },
  ];

  const activeFormat = FORMATS.find(f => f.id === selectedFormat) || FORMATS[0];

  useEffect(() => {
    loadTcgDates();
    loadBanlist("tcg_actual");
  }, []);

  const loadTcgDates = async () => {
    try {
      const res = await axios.get(`${API}/banlist/tcg/dates`);
      setTcgDates(res.data.dates || []);
    } catch (e) {}
  };

  const abortRef = useRef(null);

  const loadBanlist = async (format, date = null) => {
    // Cancel any pending request
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);
    try {
      const endpoints = {
        tcg_actual: `${API}/tcg-actual/banlist`,
        tcg: date ? `${API}/banlist?format=tcg&list_date=${date}` : `${API}/banlist?format=tcg`,
        edison: `${API}/edison/banlist`,
        goat: `${API}/goat/banlist`,
        hat: `${API}/hat/banlist`,
        speed_duel: `${API}/speed-duel/banlist`,
      };
      const res = await axios.get(endpoints[format] || endpoints.tcg_actual, { signal });
      if (!signal.aborted) setBanlist(res.data);
    } catch (e) {
      if (!axios.isCancel(e) && e.name !== 'CanceledError' && e.name !== 'AbortError') {
        console.error("Error loading banlist:", e);
      }
    }
    if (!signal.aborted) setLoading(false);
  };

  const handleFormatChange = (format) => {
    setSelectedFormat(format);
    setSelectedDate("");
    loadBanlist(format);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    loadBanlist(selectedFormat, date);
  };

  const filterCards = (cards) => {
    if (!searchTerm || !Array.isArray(cards)) return cards || [];
    return cards.filter(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  // Speed Duel has a different structure
  const isSpeedDuel = selectedFormat === "speed_duel";

  return (
    <div className="min-h-screen pt-24 pb-12" style={{ background: 'linear-gradient(180deg,#050508,#0a0512,#050508)' }} data-testid="banlist-page">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ background: `${activeFormat.color}20`, border: `1px solid ${activeFormat.color}50`, color: activeFormat.color }}>
            {activeFormat.icon} {activeFormat.name}
          </div>
          <h1 className="text-3xl md:text-5xl font-black mb-3" style={{ fontFamily: 'Cinzel,serif', background: 'linear-gradient(135deg,#fff,#d4af37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Lista de Cartas Prohibidas
          </h1>
          <p className="text-gray-400">Cartas prohibidas, limitadas y semi-limitadas por formato</p>
        </div>

        {/* Format Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {FORMATS.map(f => (
            <button
              key={f.id}
              onClick={() => handleFormatChange(f.id)}
              className="px-4 py-2 rounded-full text-sm font-bold transition-all hover:scale-105"
              style={{
                background: selectedFormat === f.id ? f.color : 'rgba(255,255,255,0.05)',
                color: selectedFormat === f.id ? (f.color === '#d4af37' ? '#000' : '#fff') : '#9ca3af',
                border: `1px solid ${selectedFormat === f.id ? f.color : 'rgba(255,255,255,0.1)'}`,
                boxShadow: selectedFormat === f.id ? `0 4px 15px ${f.color}40` : 'none',
              }}
              data-testid={`format-btn-${f.id}`}
            >
              {f.icon} {f.name}
            </button>
          ))}
        </div>

        {/* Controls: TCG date + search */}
        <div className="max-w-4xl mx-auto mb-8 glass-card rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {selectedFormat === 'tcg' && tcgDates.length > 0 && (
              <div className="flex-1">
                <label className="text-gray-400 text-xs mb-1 block">Fecha Histórica TCG</label>
                <select
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="w-full px-3 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-pink-500"
                  data-testid="tcg-date-select"
                >
                  <option value="">Banlist Actual</option>
                  {tcgDates.map((d) => <option key={d.date} value={d.date}>{d.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1">
              <label className="text-gray-400 text-xs mb-1 block">Buscar carta</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre de la carta..."
                className="w-full px-3 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-pink-500"
                data-testid="banlist-search"
              />
            </div>
          </div>
        </div>

        {/* Stats */}
        {banlist && !isSpeedDuel && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #ef4444' }}>
                <p className="text-3xl font-black text-red-400">{banlist.counts?.forbidden || 0}</p>
                <p className="text-gray-400 text-sm">Prohibidas</p>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #eab308' }}>
                <p className="text-3xl font-black text-yellow-400">{banlist.counts?.limited || 0}</p>
                <p className="text-gray-400 text-sm">Limitadas</p>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #22c55e' }}>
                <p className="text-3xl font-black text-green-400">{banlist.counts?.semi_limited || 0}</p>
                <p className="text-gray-400 text-sm">Semi-Limitadas</p>
              </div>
            </div>
          </div>
        )}

        {/* Speed Duel Stats */}
        {banlist && isSpeedDuel && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #ef4444' }}>
                <p className="text-2xl font-black text-red-400">{banlist.counts?.forbidden || 0}</p>
                <p className="text-gray-400 text-xs">Prohibidas</p>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #f59e0b' }}>
                <p className="text-2xl font-black text-amber-400">{banlist.counts?.limited_1 || 0}</p>
                <p className="text-gray-400 text-xs">Limitada 1</p>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #eab308' }}>
                <p className="text-2xl font-black text-yellow-400">{banlist.counts?.limited_2 || 0}</p>
                <p className="text-gray-400 text-xs">Limitada 2</p>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" style={{ borderLeft: '4px solid #22c55e' }}>
                <p className="text-2xl font-black text-green-400">{banlist.counts?.limited_3 || 0}</p>
                <p className="text-gray-400 text-xs">Limitada 3</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Cargando lista de cartas...</p>
          </div>
        ) : banlist ? (
          <div className="max-w-7xl mx-auto">
            {/* Standard format: Forbidden / Limited / Semi-Limited */}
            {!isSpeedDuel && (
              <>
                <BanlistSection title="Prohibidas (Forbidden)" cards={filterCards(banlist.forbidden || [])} color="red"
                  icon={<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>} />
                <BanlistSection title="Limitadas (Limited) — máx. 1 copia" cards={filterCards(banlist.limited || [])} color="yellow"
                  icon={<span className="text-white font-bold text-lg">1</span>} />
                <BanlistSection title="Semi-Limitadas — máx. 2 copias" cards={filterCards(banlist.semi_limited || [])} color="green"
                  icon={<span className="text-white font-bold text-lg">2</span>} />
              </>
            )}

            {/* Speed Duel format: Forbidden / Limited 1/2/3 */}
            {isSpeedDuel && (
              <>
                <BanlistSection title="Prohibidas (Forbidden)" cards={filterCards(banlist.forbidden || [])} color="red"
                  icon={<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>} />
                <BanlistSection title="Limitada 1 (máx. 1 copia)" cards={filterCards(banlist.limited_1 || [])} color="red"
                  icon={<span className="text-white font-bold">L1</span>} />
                <BanlistSection title="Limitada 2 (máx. 2 copias)" cards={filterCards(banlist.limited_2 || [])} color="yellow"
                  icon={<span className="text-white font-bold">L2</span>} />
                <BanlistSection title="Limitada 3 (máx. 3 copias)" cards={filterCards(banlist.limited_3 || [])} color="green"
                  icon={<span className="text-white font-bold">L3</span>} />
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400">No se pudo cargar la lista de cartas</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BanlistPage;
